import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Signal, Search, RefreshCw, Loader2, Calendar, TrendingUp,
  TrendingDown, Target, CheckCircle, XCircle, Clock, Scan,
  Zap, AlertCircle, Check, X, BarChart3, Activity, Plus, Save,
  Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const AdminSignals = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // AI Scanner states
  const [scannerStatus, setScannerStatus] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [approving, setApproving] = useState(null);
  
  // Manual Signal Form
  const [showManualForm, setShowManualForm] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [editingSignal, setEditingSignal] = useState(null); // For edit mode
  const [deletingId, setDeletingId] = useState(null);
  const [manualSignal, setManualSignal] = useState({
    symbol: 'BTC-USDT',
    action: 'BUY',
    entry_price: '',
    stop_loss: '',
    tp1: '',
    tp2: '',
    tp3: '',
    status: 'closed',
    tp1_hit: false,
    tp2_hit: false,
    tp3_hit: false,
    sl_hit: false,
    reason: '',
    signal_date: ''
  });

  // Bulk close stale data
  const [closingStale, setClosingStale] = useState(false);
  
  // Fix stuck signals (SL hit but still active)
  const [fixingStuck, setFixingStuck] = useState(false);
  const [stuckCount, setStuckCount] = useState(0);
  
  const closeStaleData = async (filter) => {
    setClosingStale(true);
    try {
      const response = await axios.post('/admin/positions/bulk-close', {
        symbol_filter: filter,
        reason: 'Eski/kullanılmayan veri temizliği',
        notify_users: false
      });
      toast.success(`${response.data.closed_count} pozisyon kapatıldı`);
      fetchSignals();
    } catch (error) {
      toast.error('Temizleme hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setClosingStale(false);
    }
  };

  const checkStuckSignals = async () => {
    try {
      const response = await axios.get('/admin/sniper/stuck-signals');
      setStuckCount(response.data.count || 0);
    } catch (error) {
      console.error('Stuck signals check error:', error);
    }
  };

  const fixStuckSignals = async () => {
    setFixingStuck(true);
    try {
      const response = await axios.post('/admin/sniper/fix-stuck-signals');
      if (response.data.fixed > 0) {
        toast.success(`${response.data.fixed} stuck sinyal düzeltildi!`);
        fetchSignals();
        setStuckCount(0);
      } else {
        toast.info('Düzeltilecek stuck sinyal bulunamadı');
      }
    } catch (error) {
      toast.error('Düzeltme hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFixingStuck(false);
    }
  };

  const symbolOptions = ['BTC-USDT', 'ETH-USDT', 'SOL-USDT', 'XRP-USDT', 'ADA-USDT', 'AVAX-USDT'];

  useEffect(() => {
    fetchSignals();
    fetchScannerStatus();
    checkStuckSignals();
  }, []);

  const handleManualInputChange = (field, value) => {
    setManualSignal(prev => ({ ...prev, [field]: value }));
    
    // Auto-set outcome based on checkboxes
    if (field === 'tp3_hit' && value) {
      setManualSignal(prev => ({ ...prev, tp1_hit: true, tp2_hit: true, tp3_hit: true, sl_hit: false, outcome_status: 'tp3' }));
    } else if (field === 'tp2_hit' && value) {
      setManualSignal(prev => ({ ...prev, tp1_hit: true, tp2_hit: true, sl_hit: false, outcome_status: 'tp2' }));
    } else if (field === 'tp1_hit' && value) {
      setManualSignal(prev => ({ ...prev, tp1_hit: true, outcome_status: 'tp1' }));
    } else if (field === 'sl_hit' && value) {
      setManualSignal(prev => ({ ...prev, sl_hit: true, outcome_status: 'stopped' }));
    }
  };

  const saveManualSignal = async () => {
    if (!manualSignal.entry_price || !manualSignal.stop_loss || !manualSignal.tp1 || !manualSignal.tp2 || !manualSignal.tp3) {
      toast.error('Lütfen tüm fiyat alanlarını doldurun');
      return;
    }
    
    setSavingManual(true);
    try {
      const payload = {
        ...manualSignal,
        entry_price: parseFloat(manualSignal.entry_price),
        stop_loss: parseFloat(manualSignal.stop_loss),
        tp1: parseFloat(manualSignal.tp1),
        tp2: parseFloat(manualSignal.tp2),
        tp3: parseFloat(manualSignal.tp3)
      };
      
      if (editingSignal) {
        await axios.put(`/admin/signals/${editingSignal.id}`, payload);
        toast.success('Sinyal başarıyla güncellendi!');
        setEditingSignal(null);
      } else {
        await axios.post('/admin/signals/manual', payload);
        toast.success('Sinyal başarıyla eklendi!');
      }
      setShowManualForm(false);
      setManualSignal({
        symbol: 'BTC-USDT',
        action: 'BUY',
        entry_price: '',
        stop_loss: '',
        tp1: '',
        tp2: '',
        tp3: '',
        status: 'closed',
        tp1_hit: false,
        tp2_hit: false,
        tp3_hit: false,
        sl_hit: false,
        reason: '',
        signal_date: ''
      });
      fetchSignals();
    } catch (error) {
      toast.error('İşlem başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingManual(false);
    }
  };

  const handleEditSignal = (signal) => {
    setEditingSignal(signal);
    setManualSignal({
      symbol: signal.symbol || 'BTC-USDT',
      action: signal.action || signal.signal_type || 'BUY',
      entry_price: signal.entry_price || signal.price || '',
      stop_loss: signal.stop_loss || signal.stop_loss_price || '',
      tp1: signal.tp1 || '',
      tp2: signal.tp2 || signal.target_price || '',
      tp3: signal.tp3 || '',
      status: signal.status || 'closed',
      tp1_hit: signal.tp1_hit || false,
      tp2_hit: signal.tp2_hit || false,
      tp3_hit: signal.tp3_hit || false,
      sl_hit: signal.sl_hit || false,
      reason: signal.reason || '',
      signal_date: signal.timestamp ? signal.timestamp.slice(0, 16) : ''
    });
    setShowManualForm(true);
  };

  const handleDeleteSignal = async (signalId) => {
    if (!window.confirm('Bu sinyali silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    setDeletingId(signalId);
    try {
      await axios.delete(`/admin/signals/${signalId}`);
      toast.success('Sinyal silindi');
      fetchSignals();
    } catch (error) {
      toast.error('Sinyal silinemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelEdit = () => {
    setShowManualForm(false);
    setEditingSignal(null);
    setManualSignal({
      symbol: 'BTC-USDT',
      action: 'BUY',
      entry_price: '',
      stop_loss: '',
      tp1: '',
      tp2: '',
      tp3: '',
      status: 'closed',
      tp1_hit: false,
      tp2_hit: false,
      tp3_hit: false,
      sl_hit: false,
      reason: '',
      signal_date: ''
    });
  };

  const fetchScannerStatus = async () => {
    try {
      const response = await axios.get('/admin/ai-signals/status');
      // Include both scanner and auto_scanner data
      setScannerStatus({
        ...response.data.scanner,
        auto_scanner: response.data.auto_scanner
      });
    } catch (error) {
      console.error('Scanner status error:', error);
    }
  };

  const runMarketScan = async () => {
    setScanning(true);
    setScanResults(null);
    try {
      const response = await axios.post('/admin/ai-signals/scan');
      setScanResults(response.data.results);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Tarama hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScanning(false);
    }
  };

  const approveSignal = async (signalData) => {
    setApproving(signalData.sembol);
    try {
      await axios.post('/admin/ai-signals/approve', { signal_data: signalData });
      toast.success(`${signalData.sembol} sinyali onaylandı ve yayınlandı!`);
      // Remove from scan results
      setScanResults(prev => ({
        ...prev,
        results: prev.results.filter(r => r.sembol !== signalData.sembol),
        signals_generated: prev.signals_generated - 1
      }));
      // Refresh signals list
      fetchSignals();
    } catch (error) {
      toast.error('Onaylama hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApproving(null);
    }
  };

  const [signalLimit, setSignalLimit] = useState(100);
  
  const fetchSignals = async (limit = signalLimit) => {
    setLoading(true);
    try {
      // include_contrarian=true to show all signals including contrarian in admin panel
      const response = await axios.get(`/signals/recent?limit=${limit}&include_contrarian=true`);
      // API returns { signals: [], total: number, data_source: {} }
      const data = response.data;
      setSignals(Array.isArray(data) ? data : (data.signals || []));
      setSignalLimit(limit);
    } catch (error) {
      console.error('Signals fetch error:', error);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadMoreSignals = () => {
    const newLimit = Math.min(signalLimit + 100, 500);
    fetchSignals(newLimit);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const filteredSignals = signals.filter(s => 
    !searchQuery || 
    s.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.strategy_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalSignals = signals.length;
  const buySignals = signals.filter(s => s.action === 'BUY' || s.signal_type === 'BUY').length;
  const sellSignals = signals.filter(s => s.action === 'SELL' || s.signal_type === 'SELL').length;
  const exitSignals = signals.filter(s => s.action === 'EXIT' || s.signal_type === 'EXIT' || s.is_exit_signal).length;
  const todaySignals = signals.filter(s => {
    const date = new Date(s.created_at || s.timestamp);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }).length;

  const getActionBadge = (action, isExit) => {
    if (action === 'EXIT' || isExit) {
      return <Badge className="bg-amber-500/20 text-amber-400"><TrendingDown className="h-3 w-3 mr-1" />ÇIKIŞ</Badge>;
    }
    if (action === 'BUY') {
      return <Badge className="bg-green-500/20 text-green-400"><TrendingUp className="h-3 w-3 mr-1" />AL</Badge>;
    }
    return <Badge className="bg-red-500/20 text-red-400"><TrendingDown className="h-3 w-3 mr-1" />SAT</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="h-3 w-3 mr-1" />Tamamlandı</Badge>;
      case 'active':
        return <Badge className="bg-blue-500/20 text-blue-400"><Clock className="h-3 w-3 mr-1" />Aktif</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />İptal</Badge>;
      default:
        return <Badge variant="outline">{status || 'Bekliyor'}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Signal className="h-6 w-6 text-primary" />
            Sinyal Yönetimi
          </h1>
          <p className="text-slate-400 text-sm">Tüm sinyalleri görüntüle ve izle</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowManualForm(!showManualForm)}>
            <Plus className="h-4 w-4 mr-2" />
            Manuel Sinyal Ekle
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fixStuckSignals}
            disabled={fixingStuck}
            className="text-orange-400 border-orange-500/40 hover:bg-orange-500/10"
          >
            {fixingStuck ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertCircle className="h-4 w-4 mr-2" />}
            Stuck Düzelt {stuckCount > 0 && `(${stuckCount})`}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => closeStaleData('TRY')}
            disabled={closingStale}
            className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10"
          >
            {closingStale ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            TRY Temizle
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => closeStaleData('USD')}
            disabled={closingStale}
            className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10"
          >
            {closingStale ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            USD Temizle
          </Button>
          <Button variant="outline" size="sm" onClick={fetchSignals}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Manual Signal Form */}
      {showManualForm && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {editingSignal ? <Edit className="h-5 w-5 text-amber-400" /> : <Plus className="h-5 w-5 text-primary" />}
              {editingSignal ? 'Sinyal Düzenle' : 'Manuel Sinyal Ekle (Geçmiş Veri)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* Symbol */}
              <div className="space-y-2">
                <Label>Sembol</Label>
                <Select value={manualSignal.symbol} onValueChange={(v) => handleManualInputChange('symbol', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {symbolOptions.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Action */}
              <div className="space-y-2">
                <Label>İşlem</Label>
                <Select value={manualSignal.action} onValueChange={(v) => handleManualInputChange('action', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">AL (Long)</SelectItem>
                    <SelectItem value="SELL">SAT (Short)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Entry Price */}
              <div className="space-y-2">
                <Label>Giriş Fiyatı ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="95000"
                  value={manualSignal.entry_price}
                  onChange={(e) => handleManualInputChange('entry_price', e.target.value)}
                />
              </div>
              
              {/* Stop Loss */}
              <div className="space-y-2">
                <Label>Stop Loss ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="93000"
                  value={manualSignal.stop_loss}
                  onChange={(e) => handleManualInputChange('stop_loss', e.target.value)}
                />
              </div>
              
              {/* TP1 */}
              <div className="space-y-2">
                <Label>TP1 ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="96000"
                  value={manualSignal.tp1}
                  onChange={(e) => handleManualInputChange('tp1', e.target.value)}
                />
              </div>
              
              {/* TP2 */}
              <div className="space-y-2">
                <Label>TP2 ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="97500"
                  value={manualSignal.tp2}
                  onChange={(e) => handleManualInputChange('tp2', e.target.value)}
                />
              </div>
              
              {/* TP3 */}
              <div className="space-y-2">
                <Label>TP3 ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="99000"
                  value={manualSignal.tp3}
                  onChange={(e) => handleManualInputChange('tp3', e.target.value)}
                />
              </div>
              
              {/* Signal Date */}
              <div className="space-y-2">
                <Label>Sinyal Tarihi</Label>
                <Input 
                  type="datetime-local"
                  value={manualSignal.signal_date}
                  onChange={(e) => handleManualInputChange('signal_date', e.target.value)}
                />
              </div>
            </div>
            
            {/* Outcome Section */}
            <div className="p-4 bg-slate-800/50 rounded-lg mb-4">
              <Label className="mb-3 block">Sonuç</Label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={manualSignal.tp1_hit}
                    onChange={(e) => handleManualInputChange('tp1_hit', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-green-400">TP1 ✓</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={manualSignal.tp2_hit}
                    onChange={(e) => handleManualInputChange('tp2_hit', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-amber-400">TP2 ✓</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={manualSignal.tp3_hit}
                    onChange={(e) => handleManualInputChange('tp3_hit', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-emerald-400">TP3 ✓</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={manualSignal.sl_hit}
                    onChange={(e) => handleManualInputChange('sl_hit', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-red-400">STOP ✗</span>
                </label>
              </div>
              
              <div className="grid grid-cols-1 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Açıklama (Opsiyonel)</Label>
                  <Input 
                    placeholder="Sinyal notu..."
                    value={manualSignal.reason}
                    onChange={(e) => handleManualInputChange('reason', e.target.value)}
                  />
                </div>
              </div>
              
              <p className="text-xs text-slate-500 mt-3">
                💡 K/Z (%) otomatik hesaplanır: Seçtiğiniz sonuç (TP1/TP2/TP3/STOP) ve fiyatlara göre.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={saveManualSignal} disabled={savingManual} className="bg-green-600 hover:bg-green-700">
                {savingManual ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingSignal ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button variant="outline" onClick={handleCancelEdit}>
                İptal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Toplam</p>
                <p className="text-2xl font-bold text-blue-400">{totalSignals}</p>
              </div>
              <Signal className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">AL Sinyali</p>
                <p className="text-2xl font-bold text-green-400">{buySignals}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">SAT Sinyali</p>
                <p className="text-2xl font-bold text-red-400">{sellSignals}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Bugün</p>
                <p className="text-2xl font-bold text-primary">{todaySignals}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Signal Scanner Section */}
      <Card className="border-purple-500/30 bg-gradient-to-r from-purple-900/10 to-transparent">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Zap className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-lg">AI Sinyal Tarayıcı</CardTitle>
                <p className="text-xs text-slate-400">GPT-4o ile teknik analiz</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Auto Scanner Status */}
              {scannerStatus?.auto_scanner?.is_running ? (
                <Badge className="bg-blue-500/20 text-blue-400">
                  <Clock className="h-3 w-3 mr-1 animate-pulse" />
                  Otomatik: Her Saat
                </Badge>
              ) : (
                <Badge className="bg-slate-500/20 text-slate-400">
                  <Clock className="h-3 w-3 mr-1" />
                  Otomatik: Kapalı
                </Badge>
              )}
              {scannerStatus?.available ? (
                <Badge className="bg-green-500/20 text-green-400">
                  <Activity className="h-3 w-3 mr-1" />
                  Hazır
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400">
                  <XCircle className="h-3 w-3 mr-1" />
                  Devre Dışı
                </Badge>
              )}
              <Button 
                onClick={runMarketScan} 
                disabled={scanning || !scannerStatus?.available}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {scanning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Taranıyor...
                  </>
                ) : (
                  <>
                    <Scan className="h-4 w-4 mr-2" />
                    Piyasayı Tara
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Auto Scanner Info */}
          {scannerStatus?.auto_scanner && (
            <div className="mb-4 p-3 bg-blue-900/20 rounded-lg border border-blue-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <span className="text-sm text-blue-300">Otomatik Tarama</span>
                </div>
                <span className={`text-xs ${scannerStatus.auto_scanner.within_active_hours ? 'text-green-400' : 'text-slate-400'}`}>
                  {scannerStatus.auto_scanner.within_active_hours ? '🟢 Aktif saatler içinde' : '⏸️ Aktif saatler dışında'}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-slate-500">Sıklık:</span>
                  <span className="text-white ml-1">Her saat başı</span>
                </div>
                <div>
                  <span className="text-slate-500">Aktif:</span>
                  <span className="text-white ml-1">{scannerStatus.auto_scanner.active_hours?.start} - {scannerStatus.auto_scanner.active_hours?.end}</span>
                </div>
                <div>
                  <span className="text-slate-500">Son tarama:</span>
                  <span className="text-white ml-1">
                    {scannerStatus.auto_scanner.last_scan_time 
                      ? new Date(scannerStatus.auto_scanner.last_scan_time).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})
                      : 'Henüz yok'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Supported Symbols */}
          {scannerStatus?.supported_symbols && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-slate-500">Desteklenen:</span>
              {scannerStatus.supported_symbols.map(s => (
                <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
              ))}
            </div>
          )}

          {/* Scan Results */}
          {scanResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-5 gap-3">
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{scanResults.symbols_scanned}</p>
                  <p className="text-xs text-slate-400">Taranan</p>
                </div>
                <div className="p-3 bg-green-900/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{scanResults.signals_generated}</p>
                  <p className="text-xs text-slate-400">Sinyal</p>
                </div>
                <div className="p-3 bg-blue-900/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">{scanResults.position_tracking || 0}</p>
                  <p className="text-xs text-slate-400">Pozisyon</p>
                </div>
                <div className="p-3 bg-amber-900/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{scanResults.wait_signals}</p>
                  <p className="text-xs text-slate-400">Bekle</p>
                </div>
                <div className="p-3 bg-red-900/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-400">{scanResults.errors}</p>
                  <p className="text-xs text-slate-400">Hata</p>
                </div>
              </div>

              {/* Individual Results */}
              <div className="space-y-3">
                {scanResults.results?.map((result, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border ${
                      result.karar === 'AL' ? 'bg-green-900/20 border-green-500/30' :
                      result.karar === 'SAT' ? 'bg-red-900/20 border-red-500/30' :
                      result.karar === 'POZİSYON_TAKİP' ? 'bg-blue-900/20 border-blue-500/30' :
                      result.karar === 'BEKLE' ? 'bg-slate-800/50 border-slate-700/50' :
                      'bg-red-900/10 border-red-500/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-white">{result.sembol}</span>
                          <Badge className={
                            result.karar === 'AL' ? 'bg-green-500/20 text-green-400' :
                            result.karar === 'SAT' ? 'bg-red-500/20 text-red-400' :
                            result.karar === 'POZİSYON_TAKİP' ? 'bg-blue-500/20 text-blue-400' :
                            result.karar === 'BEKLE' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-slate-400'
                          }>
                            {result.karar === 'AL' && <TrendingUp className="h-3 w-3 mr-1" />}
                            {result.karar === 'SAT' && <TrendingDown className="h-3 w-3 mr-1" />}
                            {result.karar === 'POZİSYON_TAKİP' && <Activity className="h-3 w-3 mr-1" />}
                            {result.karar === 'BEKLE' && <Clock className="h-3 w-3 mr-1" />}
                            {result.karar === 'POZİSYON_TAKİP' ? 'POZİSYON TAKİP' : result.karar}
                          </Badge>
                          {result.pozisyon_yonu && (
                            <Badge className={result.pozisyon_yonu === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                              {result.pozisyon_yonu === 'BUY' ? 'LONG' : 'SHORT'}
                            </Badge>
                          )}
                          {result.guven && (
                            <Badge variant="outline" className="text-xs">
                              %{result.guven} güven
                            </Badge>
                          )}
                        </div>

                        {/* Position Tracking Details */}
                        {result.karar === 'POZİSYON_TAKİP' && result.mevcut_pozisyon && (
                          <div className="mb-3">
                            {/* Status Alert */}
                            <div className={`p-2 rounded-lg mb-2 ${
                              result.durum?.includes('STOP') ? 'bg-red-900/30 border border-red-500/30' :
                              result.durum?.includes('KARDA') || result.durum?.includes('TP') ? 'bg-green-900/30 border border-green-500/30' :
                              result.durum?.includes('YAKIN') ? 'bg-amber-900/30 border border-amber-500/30' :
                              'bg-slate-800/50'
                            }`}>
                              <p className="text-sm font-medium text-white">{result.durum}</p>
                              <p className="text-xs text-slate-300">{result.tavsiye}</p>
                            </div>
                            
                            {/* Position Details */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div>
                                <p className="text-xs text-slate-500">Giriş</p>
                                <p className="font-mono text-white">${result.mevcut_pozisyon.giris_fiyati?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Güncel Fiyat</p>
                                <p className="font-mono text-white">${result.mevcut_pozisyon.guncel_fiyat?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">P/L</p>
                                <p className={`font-mono ${result.mevcut_pozisyon.pnl_yuzde >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {result.mevcut_pozisyon.pnl_yuzde >= 0 ? '+' : ''}{result.mevcut_pozisyon.pnl_yuzde}%
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Stop Mesafe</p>
                                <p className="font-mono text-amber-400">{result.mevcut_pozisyon.stop_mesafe_yuzde}%</p>
                              </div>
                            </div>
                            
                            {/* TP Levels */}
                            <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                              <div>
                                <p className="text-xs text-slate-500">Stop Loss</p>
                                <p className="font-mono text-red-400">${result.mevcut_pozisyon.stop_loss?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">TP1</p>
                                <p className="font-mono text-slate-300">${result.mevcut_pozisyon.tp1?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">TP2 (Ana)</p>
                                <p className="font-mono text-amber-400">${result.mevcut_pozisyon.tp2?.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Signal Details (for AL/SAT) */}
                        {(result.karar === 'AL' || result.karar === 'SAT') && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                            <div>
                              <p className="text-xs text-slate-500">Giriş</p>
                              <p className="font-mono text-white">${result.giris_fiyati?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Stop Loss</p>
                              <p className="font-mono text-red-400">${result.stop_loss?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">TP1 / TP2 / TP3</p>
                              <p className="font-mono text-green-400">
                                ${result.tp1?.toLocaleString()} / ${result.tp2?.toLocaleString()} / ${result.tp3?.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">R:R</p>
                              <p className="font-mono text-purple-400">1:{result.risk_odul}</p>
                            </div>
                          </div>
                        )}

                        {/* Reason */}
                        <p className="text-sm text-slate-300">{result.sebep}</p>
                        
                        {/* Technical Summary */}
                        {result.teknik_ozet && (
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <BarChart3 className="h-3 w-3" />
                            {result.teknik_ozet}
                          </p>
                        )}
                      </div>

                      {/* Approve Button (only for AL/SAT) */}
                      {(result.karar === 'AL' || result.karar === 'SAT') && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => approveSignal(result)}
                            disabled={approving === result.sembol}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {approving === result.sembol ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Onayla
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!scanResults && !scanning && (
            <div className="text-center py-8 text-slate-500">
              <Scan className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Piyasayı taramak için butona tıklayın</p>
              <p className="text-xs mt-1">AI, teknik göstergeleri analiz edip sinyal üretecek</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Sembol veya strateji ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-brand-dark border-border/40"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sinyaller ({filteredSignals.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredSignals.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Signal className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Sinyal bulunamadı</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSignals.slice(0, 50).map((signal) => (
                <div key={signal.id} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        signal.action === 'EXIT' || signal.is_exit_signal 
                          ? 'bg-amber-500/10' 
                          : signal.action === 'BUY' 
                            ? 'bg-green-500/10' 
                            : 'bg-red-500/10'
                      }`}>
                        {signal.action === 'BUY' ? (
                          <TrendingUp className="h-5 w-5 text-green-400" />
                        ) : (
                          <TrendingDown className={`h-5 w-5 ${signal.action === 'EXIT' || signal.is_exit_signal ? 'text-amber-400' : 'text-red-400'}`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{signal.symbol}</p>
                          {getActionBadge(signal.action, signal.is_exit_signal)}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>{signal.strategy_name || 'Borsamex Sinyal Sistemi'}</span>
                          {signal.source === 'sniper_engine' && (
                            <Badge className="bg-purple-500/20 text-purple-400 text-[10px] border border-purple-500/30">
                              <Zap className="h-2.5 w-2.5 mr-1" />
                              Sniper
                            </Badge>
                          )}
                          {signal.source === 'contrarian_engine' && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-[10px] border border-amber-500/30">
                              <Activity className="h-2.5 w-2.5 mr-1" />
                              Contrarian
                            </Badge>
                          )}
                          {signal.source === 'contrarian_momentum' && (
                            <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px] border border-cyan-500/30">
                              <TrendingDown className="h-2.5 w-2.5 mr-1" />
                              Momentum
                            </Badge>
                          )}
                          {signal.source === 'manual' && (
                            <Badge className="bg-slate-500/20 text-slate-400 text-[10px] border border-slate-500/30">
                              Manuel
                            </Badge>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(signal.created_at || signal.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(signal.status)}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEditSignal(signal)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-amber-400"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteSignal(signal.id)}
                        disabled={deletingId === signal.id}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-400"
                      >
                        {deletingId === signal.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Price Details Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Giriş</p>
                      <p className="font-mono text-sm text-white">${signal.entry_price?.toLocaleString() || signal.price?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Stop Loss</p>
                      <p className="font-mono text-sm text-red-400">${signal.stop_loss?.toLocaleString() || signal.stop_loss_price?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">TP1</p>
                      <p className={`font-mono text-sm ${signal.tp1_hit ? 'text-green-400' : 'text-slate-300'}`}>
                        ${signal.tp1?.toLocaleString() || '-'} {signal.tp1_hit && '✓'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">TP2 (Ana)</p>
                      <p className={`font-mono text-sm ${signal.tp2_hit ? 'text-green-400' : 'text-amber-400'}`}>
                        ${signal.tp2?.toLocaleString() || signal.target_price?.toLocaleString() || '-'} {signal.tp2_hit && '✓'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">TP3</p>
                      <p className={`font-mono text-sm ${signal.tp3_hit ? 'text-green-400' : 'text-slate-300'}`}>
                        ${signal.tp3?.toLocaleString() || '-'} {signal.tp3_hit && '✓'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Reason if available */}
                  {signal.reason && (
                    <p className="text-xs text-slate-400 mt-2 px-1">{signal.reason}</p>
                  )}
                </div>
              ))}
              
              {/* Load More Button */}
              {signals.length >= signalLimit && signalLimit < 500 && (
                <div className="flex justify-center pt-4 pb-2">
                  <Button
                    variant="outline"
                    onClick={loadMoreSignals}
                    disabled={loading}
                    className="border-slate-700 hover:bg-slate-800"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Daha Fazla Yükle ({signalLimit}/500)
                  </Button>
                </div>
              )}
              
              {/* All loaded message */}
              {signalLimit >= 500 && (
                <p className="text-center text-xs text-slate-500 pt-4">
                  Maksimum 500 sinyal gösteriliyor
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSignals;
